import type { ClaimStatus, ResearchClaim } from '../../../api/salesResearch';

const LABEL: Record<ClaimStatus, string> = {
  corroborated: 'Confirmed by two sources',
  single_source: 'Single source',
  contested: 'Sources disagree',
  contradicted: 'Contradicted',
  unverified: 'Not verified',
  grounded_internal: 'From our own material',
};

/**
 * Confidence, straight from the claim rows.
 *
 * Conflicts are expanded by default and everything else is collapsed: a rep
 * skimming before a call needs to see the disagreements without hunting for
 * them, because those are the claims that will embarrass them if repeated.
 */
export function ClaimConfidencePanel({ claims }: { claims: ResearchClaim[] }) {
  if (claims.length === 0) return null;

  const conflicts = claims.filter((c) => c.status === 'contested' || c.status === 'contradicted');
  const counts = claims.reduce<Partial<Record<ClaimStatus, number>>>((acc, c) => {
    acc[c.status] = (acc[c.status] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <section className="space-y-3">
      <h3 className="text-body-md font-semibold">Confidence</h3>

      <div className="flex flex-wrap gap-2">
        {(Object.keys(LABEL) as ClaimStatus[])
          .filter((s) => (counts[s] ?? 0) > 0)
          .map((s) => (
            <span
              key={s}
              className={`rounded-full border px-2.5 py-0.5 text-body-xs ${toneFor(s)}`}
            >
              {counts[s]} · {LABEL[s]}
            </span>
          ))}
      </div>

      {conflicts.length > 0 && (
        <div className="rounded-md border border-warning/20 bg-warning-light px-3 py-2">
          <p className="text-body-sm font-medium text-warning-dark">
            Our sources disagree on {conflicts.length} claim{conflicts.length === 1 ? '' : 's'}
          </p>
          <p className="text-body-xs text-warning-dark/80 mb-2">
            Both versions are shown. Don't repeat either as fact on a call.
          </p>
          <ul className="space-y-2">
            {conflicts.map((c) => (
              <li key={c.id} className="text-body-sm">
                <span>{c.verbatim}</span>
                {c.note && <span className="block text-body-xs text-text-tertiary">{c.note}</span>}
                {c.evidence
                  .filter((e) => e.url)
                  .map((e, i) => (
                    <a
                      key={i}
                      href={e.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mr-2 text-body-xs text-primary underline"
                    >
                      {e.provider}
                    </a>
                  ))}
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-body-xs text-text-tertiary">
        "Confirmed" means two independent providers surfaced the same claim. It does not guarantee
        the claim is true: both can repeat the same incorrect source. This is a research brief, not
        a verified fact sheet.
      </p>
    </section>
  );
}

function toneFor(status: ClaimStatus): string {
  switch (status) {
    case 'corroborated':
    case 'grounded_internal':
      return 'border-success/20 bg-success-light text-success-dark';
    case 'contested':
    case 'contradicted':
      return 'border-warning/20 bg-warning-light text-warning-dark';
    default:
      return 'border-border bg-surface-secondary text-text-secondary';
  }
}
